import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { Paginator } from '@bindoc-projects/platform-lib/data';
import {
  BdCompanyContextService,
  BdShareablesService,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  BD_SHARE_PERMISSION_DISPLAY_NAME,
  BdSharePermissionHelper,
  SHAREABLE_TYPE_DISPLAY_NAME,
  ShareableObj,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
  BdSearchProvider,
  BdSearchResult,
  provideSearchProvider,
} from './search';
import { BdShareableSelectService } from './shareable-select.service';

/**
 * Search provider that surfaces the user's accessible shareables in the global
 * search. Wraps the existing BdShareablesService query and delegates row
 * activation to BdShareableSelectService so the navigation target matches the
 * shareable's detail route.
 *
 * Each shareable maps onto the generic {@link BdSearchResult}: the `title` is
 * the shareable name, the `typeLabel` is the resource type's display name
 * ("Markt", "Klinik-Filter" …), the `description` is the user's highest
 * permission, `modifiedDate` is the shareable's last change, the `tags` are
 * the shareable's labels, and `url` is the serialized detail-page link so the
 * result page can render a real, right-clickable anchor.
 */
@Injectable()
export class BdShareableSearchProvider implements BdSearchProvider {
  public readonly order = 10;

  private readonly _shareablesService = inject(BdShareablesService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _selectService = inject(BdShareableSelectService);
  private readonly _locale = inject(LOCALE_ID);

  public search(query: string): Observable<BdSearchResult[]> {
    return this._shareablesService
      .myShareables(
        Paginator.of({
          pageSize: 50,
          query,
          sort: [Paginator.sortExpression('modifiedDate', -1)],
        }),
        undefined,
        [ShareableType.FOLDER],
        this._companyContext.currentCompanyId,
      )
      .pipe(
        take(1),
        map((page) =>
          page.content.map<BdSearchResult>((s) => ({
            id: s.id,
            title: s.name,
            tags: s.labels?.map((label) => label.text),
            typeLabel: SHAREABLE_TYPE_DISPLAY_NAME[s.type] ?? s.type,
            description: this._highestPermission(s),
            modifiedDate: s.modifiedDate,
            url: this._selectService.buildDetailUrl(s.id, s.type),
            open: () => this._selectService.navigate(s),
          })),
        ),
      );
  }

  /**
   * The user's highest permission on the shareable in localized wording (e.g.
   * "Kann bearbeiten"), or an empty string when it carries none. Permissions are
   * cumulative, so the highest one alone describes the access.
   */
  private _highestPermission(shareable: ShareableObj): string {
    const permissions = shareable.allowedShareablePermissions;
    if (!permissions || permissions.length === 0) {
      return '';
    }

    const highest = BdSharePermissionHelper.getHighestPermission(permissions);
    const localized =
      BD_SHARE_PERMISSION_DISPLAY_NAME[this._locale] ??
      BD_SHARE_PERMISSION_DISPLAY_NAME['de'];

    return localized[highest];
  }
}

/** Registers BdShareableSearchProvider on the BD_SEARCH_PROVIDERS multi-token. */
export function provideShareableSearch() {
  return provideSearchProvider(BdShareableSearchProvider);
}
